import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Loader2, Copy, Check, Save, Sparkles, ExternalLink, AlertCircle, FileText, FileDown, Info, RefreshCw, User, Bot, Globe, BookOpen, MessageCircle } from "lucide-react";
import { SummaryRenderer } from "./SummaryRenderer";
import { FollowUpChat } from "./FollowUpChat";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { VideoResult } from "./types";

const EXPECTED_SECTIONS = ["Executive Overview", "Core Arguments", "Key Data", "Deep Analysis", "Strategic Takeaways", "Critical Assessment", "Classification"];

function AnalysisProgressBar({ summary }: { summary: string }) {
  const sectionsFound = useMemo(() => {
    if (!summary) return 0;
    return EXPECTED_SECTIONS.filter(s => summary.toLowerCase().includes(s.toLowerCase())).length;
  }, [summary]);

  const progress = summary
    ? Math.min(95, Math.round((sectionsFound / EXPECTED_SECTIONS.length) * 100))
    : 5;

  const currentStep = !summary
    ? "Fetching transcript…"
    : sectionsFound === 0
      ? "Analyzing content…"
      : `Generating section ${sectionsFound + 1} of ${EXPECTED_SECTIONS.length}…`;

  return (
    <div className="space-y-2 mb-3">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin text-primary" />
          {currentStep}
        </span>
        <span className="font-medium">{progress}%</span>
      </div>
      <Progress value={progress} className="h-2" />
    </div>
  );
}

interface Props {
  result: VideoResult;
  userId?: string;
  onSaved: (id: string) => void;
  onRetry?: (id: string) => void;
}

export function VideoResultCard({ result, userId, onSaved, onRetry }: Props) {
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const { toast } = useToast();

  const handleCopy = () => {
    navigator.clipboard.writeText(result.summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportMarkdown = () => {
    const md = `# ${result.url}\n\n${result.summary}`;
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `summary-${result.videoId || "video"}-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    const { default: jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const maxWidth = pageWidth - margin * 2;
    let y = 20;

    const addText = (text: string, fontSize: number, isBold = false) => {
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", isBold ? "bold" : "normal");
      const lines = doc.splitTextToSize(text, maxWidth);
      for (const line of lines) {
        if (y > 275) { doc.addPage(); y = 20; }
        doc.text(line, margin, y);
        y += fontSize * 0.45;
      }
      y += 3;
    };

    addText(result.url, 12, true);
    y += 4;
    const plain = result.summary
      .replace(/^#{1,6}\s+/gm, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/^[-*]\s+/gm, "• ")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
    for (const p of plain.split(/\n\n+/)) {
      const trimmed = p.trim();
      if (!trimmed) continue;
      addText(trimmed, 10);
    }
    doc.save(`summary-${result.videoId || "video"}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handleSave = async () => {
    if (!userId) {
      toast({ title: "Sign in required", description: "Please sign in to save summaries.", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("content_summaries").insert({
      user_id: userId,
      video_url: result.url,
      video_id: result.videoId,
      summary_content: result.summary,
      author: result.author || null,
    });
    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } else {
      onSaved(result.id);
      toast({ title: "Saved", description: "Summary saved to Collected Data." });
    }
  };

  if (result.status === "error") {
    return (
      <Card className="border-destructive/30">
        <CardContent className="p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-destructive">Failed</p>
            <p className="text-xs text-muted-foreground truncate">{result.url}</p>
            <p className="text-xs text-destructive/70 mt-1">{result.error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/10">
      {/* Thumbnail bar */}
      {result.videoId && (
        <div className="flex items-center gap-3 px-4 pt-4">
          <img
            src={`https://img.youtube.com/vi/${result.videoId}/mqdefault.jpg`}
            alt="Thumbnail"
            className="w-28 h-16 object-cover rounded-lg shadow-sm"
          />
          <div className="flex-1 min-w-0 space-y-1">
            {result.author && (
              <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                <User className="h-3 w-3 text-muted-foreground" />
                {result.author}
              </div>
            )}
            <a
              href={`https://www.youtube.com/watch?v=${result.videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline flex items-center gap-1 truncate"
            >
              Open on YouTube <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
            <a
              href={`https://notebooklm.google.com/?pli=1#source=${encodeURIComponent(`https://www.youtube.com/watch?v=${result.videoId}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline flex items-center gap-1 truncate"
            >
              Open in NotebookLM <BookOpen className="h-3 w-3 shrink-0" />
            </a>
            <p className="text-[10px] text-muted-foreground truncate">{result.url}</p>
          </div>
          <Badge variant={result.status === "done" ? "default" : "secondary"} className="shrink-0">
            {result.status === "loading" ? "Analyzing…" : result.status === "done" ? "Complete" : "Pending"}
          </Badge>
        </div>
      )}

      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            AI Summary
          </CardTitle>
          {result.model && (
            <Badge variant="outline" className="text-[10px] gap-1 font-normal">
              <Bot className="h-3 w-3" />
              {result.model}
            </Badge>
          )}
          {result.language && result.language !== "english" && (
            <Badge variant="outline" className="text-[10px] gap-1 font-normal capitalize">
              <Globe className="h-3 w-3" />
              {result.language}
            </Badge>
          )}
        </div>
        {result.status === "done" && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setShowChat(c => !c)} className={showChat ? "text-primary" : ""}>
              <MessageCircle className="h-4 w-4 mr-1" />
              Q&A
            </Button>
            <Button variant="ghost" size="sm" onClick={handleSave} disabled={saving || result.saved}>
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : result.saved ? <Check className="h-4 w-4 mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              {saving ? "Saving…" : result.saved ? "Saved" : "Save"}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleCopy}>
              {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleExportMarkdown} title="Export as Markdown">
              <FileText className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleExportPdf} title="Export as PDF">
              <FileDown className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {result.limitedInfo && result.status === "done" && (
          <div className="flex items-start gap-2 p-3 mb-3 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1 text-xs">
              Captions were unavailable for this video. This summary is based on limited information (title/description only) and may not fully reflect the video's content.
            </div>
            {onRetry && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 h-7 text-xs gap-1"
                onClick={() => onRetry(result.id)}
              >
                <RefreshCw className="h-3 w-3" />
                Retry
              </Button>
            )}
          </div>
        )}
        {result.status === "loading" && (
          <AnalysisProgressBar summary={result.summary} />
        )}
        {result.summary && (
          <SummaryRenderer summary={result.summary} isStreaming={result.status === "loading"} />
        )}
        {showChat && result.status === "done" && (
          <FollowUpChat
            summary={result.summary}
            videoUrl={result.url}
            onClose={() => setShowChat(false)}
          />
        )}
      </CardContent>
    </Card>
  );
}
